import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import { useRef } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import { RoiId } from '~/domain/changes/ids';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { selectedROIState } from '~/state/atoms/structureSet/roiAtoms';
import StructureDeletionConfirmationDialog from './StructureDeletionConfirmationDialog';
import { LEADING_COLUMN_PX } from './leadingColumn';
import StructureProperties from './StructureProperties';

export default function StructureLayer({
    roi,
    roiId,
    isMultiSelected = false,
    showSelectionCheckbox = false,
    onSelectionInput,
}: {
    roi: RoiReadModel;
    roiId?: RoiId;
    isMultiSelected?: boolean;
    /**
     * Show the row's membership as a checkbox. Set while multi-select mode
     * is armed, where a plain click toggles membership: without it the row
     * tint is the only sign a structure is in the selection, which reads
     * as a hover state rather than a choice the user made.
     */
    showSelectionCheckbox?: boolean;
    onSelectionInput?: (
        roiNumber: number,
        mods: { ctrl: boolean; shift: boolean }
    ) => boolean;
}) {
    const intl = useIntl();
    const roiFieldRef = useRef<HTMLDivElement | null>(null);
    const selectedROI = useRecoilValue<number>(selectedROIState);
    const ROINumber = roi.roiNumber;
    const isSelected = selectedROI === ROINumber;
    // The row's Delete-key handler also clears contours on the current
    // slice, so the geometry guard gates both.
    const structureCanBeDeleted = roi.canModifyContours;

    return (
        <Box
            ref={roiFieldRef}
            display="flex"
            alignItems="center"
            data-structure-row
            data-roi-number={ROINumber}
            role="option"
            aria-selected={isSelected || isMultiSelected}
            sx={(theme) => ({
                height: 26,
                px: 0.25,
                gap: 0.5,
                borderRadius: `${theme.shape.borderRadius}px`,
                backgroundColor:
                    isSelected || isMultiSelected
                        ? theme.palette.action.selected
                        : 'transparent',
                '&:hover': {
                    backgroundColor:
                        isSelected || isMultiSelected
                            ? theme.palette.action.selected
                            : theme.palette.action.hover,
                },
            })}
        >
            {showSelectionCheckbox && (
                <Checkbox
                    size="small"
                    checked={isMultiSelected}
                    // The row itself toggles too, so the box reports the
                    // same plain-click input rather than a second path
                    // into the selection.
                    onClick={() =>
                        onSelectionInput?.(ROINumber, {
                            ctrl: false,
                            shift: false,
                        })
                    }
                    inputProps={{
                        'aria-label': intl.formatMessage(
                            { id: 'structure.multiSelect.rowCheckbox' },
                            { name: roi.name }
                        ),
                    }}
                    data-testid={`structure-checkbox-${ROINumber}`}
                    // A column as wide as the dot's, the box as big as the
                    // dot, so the two leading marks line up and match.
                    sx={{
                        p: 0,
                        width: LEADING_COLUMN_PX,
                        height: 20,
                        flexShrink: 0,
                        '& .MuiSvgIcon-root': { fontSize: 16 },
                    }}
                />
            )}
            <StructureProperties
                roi={roi}
                roiId={roiId}
                onSelectionInput={onSelectionInput}
            />
            {structureCanBeDeleted && (
                <StructureDeletionConfirmationDialog
                    roi={roi}
                    roiId={roiId}
                    isSelected={isSelected}
                />
            )}
        </Box>
    );
}
